import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  BRAND_GRADIENTS,
  BRAND_TONE,
  CardBrand,
  CENTERED_ICON,
  COLORS,
  MaterialIcon,
  type MaterialIconType,
  RADIUS,
} from '@repo/ui';
import { formatter } from '@repo/utils';

/**
 * Shared shell for the review cards: filled card with a dark caption and an optional round edit action.
 * Outlined in red when `error` is set; `hero` swaps the grey fill for the brand wash of the order card.
 */
export const DetailCard = ({
  label,
  editLabel,
  onEdit,
  error,
  hero,
  children,
}: {
  label: string;
  editLabel?: string;
  onEdit?: () => void;
  error?: boolean;
  hero?: boolean;
  children: React.ReactNode;
}) => (
  <Stack
    sx={{
      gap: 1.5,
      p: 2.5,
      borderRadius: `${RADIUS.xl}px`,
      // Borderless filled card; the transparent border keeps the size when the error outline shows
      border: `1px solid ${error ? COLORS.state.error : 'transparent'}`,
      background: hero ? BRAND_GRADIENTS.cardWash() : COLORS.surface.card,
    }}
  >
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 36 }}>
      {/* Dark semibold titles: thin brand-colored ones read as links next to the card actions */}
      <Typography component="p" variant="body1" sx={{ fontWeight: 600, color: COLORS.text.main }}>
        {label}
      </Typography>
      {onEdit && (
        <Tooltip title={editLabel}>
          <IconButton
            aria-label={editLabel}
            onClick={onEdit}
            sx={{ width: 36, height: 36, backgroundColor: COLORS.surface.raised }}
          >
            <MaterialIcon icon="edit" size={20} sx={{ ...CENTERED_ICON, color: COLORS.text.brand }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
    {children}
  </Stack>
);

/** Badge-sized lavender tile for an icon, same footprint as the card network badge. */
const IconTile = ({ icon }: { icon: MaterialIconType }) => (
  <Box
    aria-hidden
    sx={{
      ...CENTERED_ICON,
      flexShrink: 0,
      width: 42,
      height: 28,
      borderRadius: '4px',
      backgroundColor: COLORS.surface.raised,
    }}
  >
    <MaterialIcon icon={icon} size={18} sx={{ ...CENTERED_ICON, color: COLORS.text.brand }} />
  </Box>
);

/** Badge on the left, bold title and an optional grey line under it. */
const DetailRow = ({
  badge,
  title,
  subtitle,
  subtitleLabel,
}: {
  badge: React.ReactNode;
  title: string;
  subtitle?: string;
  subtitleLabel?: string;
}) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
    {badge}
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="span" variant="body1" sx={{ fontWeight: 600, color: COLORS.text.main }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          component="span"
          variant="subtitle2"
          aria-label={subtitleLabel}
          sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.5px', overflowWrap: 'anywhere' }}
        >
          {subtitle}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const BRAND_NAMES: Record<string, string> = { visa: 'Visa', master: 'Mastercard', amex: 'American Express' };

// Issuers come as codes like "MASTERCARD_DEBIT": turn them into "Mastercard débito"
const cardLabel = (issuer = '') => {
  const value = issuer.toLowerCase();
  const brand =
    Object.entries(BRAND_NAMES).find(([key]) => value.includes(key))?.[1] ||
    value.split(/[_\s]/)[0]?.replace(/^./, (c) => c.toUpperCase()) ||
    'Tarjeta';
  const type = value.includes('debit') ? ' débito' : value.includes('credit') ? ' crédito' : '';
  return `${brand}${type}`;
};

/** Chosen payment method: network badge, readable card name and masked number, or the cash option. */
export const PaymentMethodCard = ({
  paymentMethod,
  cardIssuer,
  cardNumber,
  error,
  onEdit,
}: {
  paymentMethod?: string;
  cardIssuer?: string;
  cardNumber?: string;
  error?: boolean;
  onEdit?: () => void;
}) => {
  const isCash = paymentMethod === 'CASH';
  const lastDigits = cardNumber?.slice(-4);

  return (
    <DetailCard
      label={isCash ? 'Tus cuotas se pagan' : 'Tus cuotas se debitan de'}
      editLabel="Cambiar método de pago"
      onEdit={onEdit}
      error={error}
    >
      {isCash ? (
        <DetailRow badge={<IconTile icon="payments" />} title="En locales habilitados o Pago Digital" />
      ) : (
        <DetailRow
          badge={<CardBrand issuer={cardIssuer} />}
          title={cardLabel(cardIssuer)}
          subtitle={lastDigits ? `•••• •••• •••• ${lastDigits}` : undefined}
          subtitleLabel={lastDigits ? `terminada en ${lastDigits}` : undefined}
        />
      )}
    </DetailCard>
  );
};

/** Account the loan is deposited into. */
export const BankAccountCard = ({ cbu, onEdit }: { cbu: string; onEdit?: () => void }) => (
  <DetailCard label="Te lo depositamos en" editLabel="Cambiar cuenta" onEdit={onEdit}>
    <DetailRow badge={<IconTile icon="account_balance" />} title="Cuenta bancaria" subtitle={`CBU ${cbu}`} />
  </DetailCard>
);

/** Loan rates in three equal columns; the total cost rate stands out by size and weight only, as regulation asks. */
export const RatesCard = ({ tea, tna, cft }: { tea: number; tna: number; cft: number }) => {
  const rates = [
    { label: 'TEA', value: tea },
    { label: 'TNA', value: tna },
    { label: 'CFTTEA', value: cft, emphasis: true },
  ];

  return (
    <DetailCard label="Tasas de tu préstamo">
      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, m: 0 }}>
        {rates.map(({ label, value, emphasis }) => (
          <Stack key={label} sx={{ gap: 0.25, alignItems: emphasis ? 'flex-end' : 'flex-start' }}>
            <Typography
              component="dt"
              variant="subtitle2"
              sx={emphasis ? { fontWeight: 700, fontSize: 16 } : undefined}
            >
              {label}
            </Typography>
            <Typography
              component="dd"
              sx={{
                typography: 'h2',
                letterSpacing: '0.01em',
                m: 0,
                fontSize: emphasis ? 18 : 16,
                lineHeight: emphasis ? '24px' : '22px',
                // Regular TEA and TNA (light was too faint on phones), medium 18px CFTTEA
                fontWeight: emphasis ? 500 : 400,
                // Dark, not brand: violet on the grey card is under 4.5:1 at this size
                color: COLORS.text.main,
              }}
            >
              {`${formatter(value, 'number')}%`}
            </Typography>
          </Stack>
        ))}
      </Box>
    </DetailCard>
  );
};

/** Amount with the cents smaller and faded, as on the order and plan cards. */
const Amount = ({ value, size, color }: { value?: number; size: number; color: string }) => {
  const [integer, cents] = formatter(value?.toString(), 'currency').split(',');
  const text = { typography: 'h2', letterSpacing: '0.01em', fontWeight: 700, color };
  return (
    <Stack direction="row" sx={{ alignItems: 'flex-start', flexShrink: 0 }}>
      <Typography component="span" sx={{ ...text, fontSize: size, lineHeight: `${size + 6}px` }}>
        {integer}
      </Typography>
      {cents && (
        <Typography
          component="span"
          sx={{
            ...text,
            fontSize: Math.round(size * 0.55),
            lineHeight: `${Math.round(size * 0.55) + 6}px`,
            // Faded blue still clears 4.5:1; faded violet wouldn't, so B2C cents stay solid
            opacity: BRAND_TONE === 'b2b' ? 0.75 : 1,
          }}
        >
          ,{cents}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Loan summary for the efectivo review: requested amount and installment count as plain rows, the monthly
 * payment in a white band, the first due date as a note, on the brand wash of the order card.
 */
export const LoanSummaryCard = ({
  requestedAmount,
  installmentCount = 0,
  installmentAmount,
  firstDueDate,
  onEdit,
}: {
  requestedAmount?: number;
  installmentCount?: number;
  installmentAmount?: number;
  firstDueDate?: string;
  onEdit?: () => void;
}) => (
  <DetailCard label="Tu préstamo" editLabel="Cambiar monto y plan de cuotas" onEdit={onEdit} hero>
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      <Typography component="span" variant="subtitle2">
        Monto solicitado
      </Typography>
      <Amount value={requestedAmount} size={20} color={COLORS.text.brand} />
    </Stack>
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      <Typography component="span" variant="subtitle2">
        Plazo
      </Typography>
      <Typography
        component="span"
        sx={{
          typography: 'h2',
          letterSpacing: '0.01em',
          fontSize: 20,
          lineHeight: '26px',
          fontWeight: 700,
          color: COLORS.text.brand,
        }}
      >
        {installmentCount} {installmentCount === 1 ? 'cuota' : 'cuotas'}
      </Typography>
    </Stack>
    {/* The monthly payment is what the user commits to: white band, purple amount */}
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        mt: 0.5,
        px: 2,
        py: 1.5,
        borderRadius: `${RADIUS.lg}px`,
        backgroundColor: COLORS.surface.raised,
      }}
    >
      <Stack>
        <Typography component="span" variant="body1" sx={{ fontWeight: 600, color: COLORS.text.main }}>
          Cuota mensual
        </Typography>
        {firstDueDate && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <MaterialIcon icon="calendar_month" size={16} sx={{ ...CENTERED_ICON, color: COLORS.text.brand }} />
            <Typography component="span" variant="subtitle2" sx={{ fontSize: 12 }}>
              Primera el {formatter(firstDueDate, 'date')}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Amount value={installmentAmount} size={28} color={COLORS.text.brand} />
    </Stack>
  </DetailCard>
);
